import React, { useState, useEffect } from 'react';
import { perizinanIptService } from '../../services/perizinanIptService';
import {
    FaDownload, FaFilePdf, FaCheckCircle, FaClock,
    FaMoneyBillWave, FaUser, FaBuilding, FaFileAlt,
    FaCalendarCheck, FaInfoCircle, FaSpinner,
    FaExclamationTriangle, FaPhone, FaWhatsapp, FaPaperPlane
} from 'react-icons/fa';

const PerizinanIPT = () => {
    const [activeTab, setActiveTab] = useState('persyaratan');
    const [showForm, setShowForm] = useState(false);

    // ✅ State dari API
    const [setting, setSetting] = useState(null);
    const [files, setFiles] = useState([]);
    const [faqs, setFaqs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // ✅ State untuk form submission
    const [formData, setFormData] = useState({
        nama_lengkap: '',
        nik: '',
        nomor_telepon: '',
        email: '',
        alamat_lengkap: '',
        lokasi_tanah: '',
        luas_tanah: '',
        tujuan_penggunaan: '',
        website_url: '', // 🍯 Honeypot — WAJIB KOSONG
    });
    const [formFiles, setFormFiles] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [submitResult, setSubmitResult] = useState(null);
    const [submitError, setSubmitError] = useState('');
    const [formErrors, setFormErrors] = useState({});

    // ✅ Fetch data dari API saat mount
    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        setError('');
        try {
            const result = await perizinanIptService.getPublic();
            if (result.success) {
                const data = result.data || {};
                setSetting(data.setting || null);
                setFiles(data.files || []);
                setFaqs(data.faqs || []);
            } else {
                setError(result.message || 'Gagal memuat data');
            }
        } catch (err) {
            setError('Terjadi kesalahan saat memuat data');
        }
        setLoading(false);
    };

    const getFileUrl = (path) => {
        if (!path) return '#';
        if (path.startsWith('http')) return path;
        return `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/${path}`;
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return '-';
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    // ============ FORM HANDLERS ============

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (formErrors[name]) {
            setFormErrors(prev => ({ ...prev, [name]: '' }));
        }
        // Reset submit result & error ketika user ubah input
        if (submitResult) setSubmitResult(null);
        if (submitError) setSubmitError('');
    };

    const handleFormFilesChange = (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length > 5) {
            setSubmitError('Maksimal 5 file dokumen pendukung');
            return;
        }

        // Validasi client-side: size & type
        const invalid = files.filter(f => {
            const allowed = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
            return !allowed.includes(f.type) || f.size > 5 * 1024 * 1024;
        });

        if (invalid.length > 0) {
            setSubmitError('Ada file yang tidak valid (harus PDF/JPG/PNG maks 5MB)');
            return;
        }

        setFormFiles(files);
        setSubmitError('');
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setSubmitError('');
        setSubmitResult(null);
        setFormErrors({});

        // ✅ Validasi client-side
        const errors = {};
        if (!formData.nama_lengkap || formData.nama_lengkap.trim().length < 3) {
            errors.nama_lengkap = 'Nama minimal 3 karakter';
        }
        if (!/^[0-9]{16}$/.test(formData.nik)) {
            errors.nik = 'NIK harus 16 digit angka';
        }
        if (!formData.nomor_telepon || formData.nomor_telepon.length < 10) {
            errors.nomor_telepon = 'Nomor telepon minimal 10 digit';
        }
        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            errors.email = 'Format email tidak valid';
        }
        if (!formData.alamat_lengkap || formData.alamat_lengkap.trim().length < 10) {
            errors.alamat_lengkap = 'Alamat minimal 10 karakter';
        }
        if (!formData.lokasi_tanah) {
            errors.lokasi_tanah = 'Lokasi tanah wajib diisi';
        }
        if (!formData.luas_tanah || parseFloat(formData.luas_tanah) <= 0) {
            errors.luas_tanah = 'Luas tanah harus lebih dari 0';
        }
        if (!formData.tujuan_penggunaan || formData.tujuan_penggunaan.trim().length < 10) {
            errors.tujuan_penggunaan = 'Tujuan minimal 10 karakter';
        }

        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            setSubmitError('Periksa kembali data yang diisi');
            setSubmitting(false);
            // Scroll ke form
            document.getElementById('form-online')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            return;
        }

        try {
            const fd = new FormData();
            Object.entries(formData).forEach(([k, v]) => fd.append(k, v));
            formFiles.forEach(f => fd.append('file_pendukung[]', f));

            const result = await perizinanIptService.submitPermohonan(fd);

            if (result.success) {
                setSubmitResult(result.data);
                // Reset form
                setFormData({
                    nama_lengkap: '',
                    nik: '',
                    nomor_telepon: '',
                    email: '',
                    alamat_lengkap: '',
                    lokasi_tanah: '',
                    luas_tanah: '',
                    tujuan_penggunaan: '',
                    website_url: '',
                });
                setFormFiles([]);
                // Scroll ke hasil
                setTimeout(() => {
                    document.getElementById('form-online')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 100);
            } else {
                setSubmitError(result.message || 'Gagal mengirim permohonan');
                if (result.errors) {
                    // Map error dari Laravel validation
                    const mapped = {};
                    Object.entries(result.errors).forEach(([k, v]) => {
                        mapped[k] = Array.isArray(v) ? v[0] : v;
                    });
                    setFormErrors(mapped);
                }
            }
        } catch (err) {
            setSubmitError('Terjadi kesalahan. Silakan coba lagi.');
        }
        setSubmitting(false);
    };

    // ============ TABS ============
    const tabs = [
        { id: 'persyaratan', label: 'Persyaratan', icon: FaFileAlt },
        { id: 'prosedur', label: 'Prosedur', icon: FaCheckCircle },
        { id: 'biaya', label: 'Biaya & Waktu', icon: FaClock },
        { id: 'formulir', label: 'Formulir', icon: FaDownload },
    ];

    // ============ STATIC: PERSYARATAN ============
    const persyaratanList = [
        {
            kategori: 'Dokumen Pemohon',
            items: [
                'Fotokopi KTP yang masih berlaku (1 lembar)',
                'Fotokopi NPWP (jika ada)',
                'Pas foto berwarna ukuran 3x4 (2 lembar)',
                'Surat kuasa bermaterai (jika dikuasakan)',
            ],
        },
        {
            kategori: 'Dokumen Teknis',
            items: [
                'Surat permohonan tertulis kepada Kepala BPKAD',
                'Proposal rencana penggunaan tanah',
                'Gambar site plan/lokasi tanah',
                'Denah bangunan (jika akan mendirikan bangunan)',
                'Fotokopi sertifikat tanah (jika ada)',
            ],
        },
        {
            kategori: 'Dokumen Pendukung',
            items: [
                'Surat rekomendasi dari kelurahan setempat',
                'Surat pernyataan tidak keberatan dari pihak terkait',
                'Surat pernyataan sanggup membayar retribusi',
                'Dokumen lingkungan (AMDAL/UKL-UPL jika diperlukan)',
            ],
        },
    ];

    // ============ STATIC: PROSEDUR ============
    const prosedurList = [
        {
            step: 1,
            title: 'Pengajuan Permohonan',
            description: 'Pemohon mengajukan surat permohonan dan melengkapi dokumen persyaratan ke kantor BPKAD Kota Magelang',
            icon: FaUser,
            waktu: 'Hari ke-1',
        },
        {
            step: 2,
            title: 'Verifikasi Dokumen',
            description: 'Petugas melakukan verifikasi kelengkapan dan keabsahan dokumen yang diajukan',
            icon: FaFileAlt,
            waktu: 'Hari ke-2 s/d 3',
        },
        {
            step: 3,
            title: 'Survey Lapangan',
            description: 'Tim teknis BPKAD melakukan peninjauan dan pengukuran lokasi tanah yang dimohon',
            icon: FaBuilding,
            waktu: 'Hari ke-4 s/d 7',
        },
        {
            step: 4,
            title: 'Penilaian dan Perhitungan',
            description: 'Tim melakukan penilaian kelayakan dan menghitung besaran retribusi sesuai peraturan yang berlaku',
            icon: FaMoneyBillWave,
            waktu: 'Hari ke-8 s/d 10',
        },
        {
            step: 5,
            title: 'Penetapan dan Pembayaran',
            description: 'Penerbitan SKRD (Surat Ketetapan Retribusi Daerah) dan pemohon melakukan pembayaran retribusi',
            icon: FaCheckCircle,
            waktu: 'Hari ke-11 s/d 14',
        },
        {
            step: 6,
            title: 'Penerbitan Izin',
            description: 'Penerbitan Surat Izin Pemakaian Tanah (IPT) setelah pembayaran diverifikasi',
            icon: FaCalendarCheck,
            waktu: 'Hari ke-15 s/d 21',
        },
    ];

    // ============ DYNAMIC: BIAYA (dari setting) ============
    const biayaInfo = setting
        ? [
            {
                jenis: 'Retribusi IPT',
                tarif: setting.biaya_retribusi_tarif || '-',
                keterangan: setting.biaya_retribusi_keterangan || '',
                icon: FaMoneyBillWave,
            },
            {
                jenis: 'Biaya Administrasi',
                tarif: setting.biaya_administrasi_tarif || '-',
                keterangan: setting.biaya_administrasi_keterangan || '',
                icon: FaFileAlt,
            },
            {
                jenis: 'Biaya Survey',
                tarif: setting.biaya_survey_tarif || '-',
                keterangan: setting.biaya_survey_keterangan || '',
                icon: FaBuilding,
            },
        ]
        : [];

    // ============ DYNAMIC: WAKTU (dari setting) ============
    const waktuInfo = setting
        ? [
            { label: 'Verifikasi Dokumen', value: setting.waktu_verifikasi || '-', bg: 'bg-green-50', color: 'text-green-600' },
            { label: 'Survey & Penilaian', value: setting.waktu_survey || '-', bg: 'bg-blue-50', color: 'text-blue-600' },
            { label: 'Penerbitan Izin', value: setting.waktu_penerbitan || '-', bg: 'bg-purple-50', color: 'text-purple-600' },
        ]
        : [];

    // ============ DYNAMIC: FAQ (dari API) ============
    const faqList = faqs.map(f => ({
        question: f.pertanyaan,
        answer: f.jawaban,
    }));

    // ============ RENDER: LOADING ============
    if (loading) {
        return (
            <div className="container mx-auto px-4 py-20 text-center">
                <FaSpinner className="animate-spin text-5xl text-[#0D9488] mx-auto mb-4" />
                <p className="text-gray-500">Memuat informasi layanan...</p>
            </div>
        );
    }

    // ============ RENDER: ERROR ============
    if (error) {
        return (
            <div className="container mx-auto px-4 py-20">
                <div className="card-glass text-center py-12 max-w-xl mx-auto">
                    <FaExclamationTriangle className="text-5xl text-yellow-500 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-gray-700 mb-2">Gagal Memuat Data</h2>
                    <p className="text-gray-500">{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-12">
            {/* Header */}
            <div className="mb-8">
                <h1 className="text-3xl md:text-4xl font-bold text-[#1E3A8A] mb-4">
                    {setting?.header_judul || 'Perizinan Pemakaian Aset Tanah (IPT)'}
                </h1>
                <p className="text-gray-600 text-lg">
                    {setting?.header_deskripsi ||
                        'Layanan perizinan penggunaan tanah aset Pemerintah Kota Magelang untuk masyarakat dan badan usaha'}
                </p>
            </div>

            {/* Tab Navigation */}
            <div className="flex flex-wrap gap-2 mb-8 border-b border-gray-200">
                {tabs.map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center px-6 py-3 text-sm font-medium transition-all duration-200 border-b-2 ${activeTab === tab.id
                                ? 'border-[#0D9488] text-[#0D9488] bg-[#0D9488]/5'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                    >
                        <tab.icon className="mr-2" />
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Tab Content */}
            <div className="space-y-8">
                {/* ============ TAB: PERSYARATAN ============ */}
                {activeTab === 'persyaratan' && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {persyaratanList.map((kategori, index) => (
                            <div key={index} className="card-glass">
                                <h3 className="text-xl font-bold text-[#1E3A8A] mb-4 flex items-center">
                                    <FaFileAlt className="mr-2 text-[#0D9488]" />
                                    {kategori.kategori}
                                </h3>
                                <ul className="space-y-3">
                                    {kategori.items.map((item, idx) => (
                                        <li key={idx} className="flex items-start">
                                            <FaCheckCircle className="text-[#0D9488] mt-1 mr-2 flex-shrink-0" />
                                            <span className="text-gray-700">{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                )}

                {/* ============ TAB: PROSEDUR ============ */}
                {activeTab === 'prosedur' && (
                    <div className="relative">
                        <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-[#0D9488]/20 hidden md:block"></div>

                        <div className="space-y-8">
                            {prosedurList.map((prosedur, index) => (
                                <div key={index} className="relative flex items-start">
                                    <div className="flex-shrink-0 w-16 h-16 bg-[#0D9488] rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg z-10">
                                        {prosedur.step}
                                    </div>
                                    <div className="ml-6 card-glass flex-1">
                                        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-3">
                                            <h3 className="text-xl font-bold text-[#1E3A8A] flex items-center">
                                                <prosedur.icon className="mr-2 text-[#0D9488]" />
                                                {prosedur.title}
                                            </h3>
                                            <span className="text-sm bg-[#0D9488]/10 text-[#0D9488] px-3 py-1 rounded-full font-medium mt-2 md:mt-0 inline-block w-fit">
                                                <FaClock className="inline mr-1" />
                                                {prosedur.waktu}
                                            </span>
                                        </div>
                                        <p className="text-gray-600">{prosedur.description}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* ============ TAB: BIAYA & WAKTU ============ */}
                {activeTab === 'biaya' && (
                    <div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                            {biayaInfo.map((biaya, index) => (
                                <div key={index} className="card-glass text-center">
                                    <div className="text-4xl text-[#0D9488] mb-4 flex justify-center">
                                        <biaya.icon />
                                    </div>
                                    <h3 className="text-xl font-bold text-[#1E3A8A] mb-2">
                                        {biaya.jenis}
                                    </h3>
                                    <p className="text-2xl font-bold text-[#0D9488] mb-2">
                                        {biaya.tarif}
                                    </p>
                                    <p className="text-gray-600 text-sm">{biaya.keterangan}</p>
                                </div>
                            ))}
                        </div>

                        <div className="card-glass">
                            <h3 className="text-xl font-bold text-[#1E3A8A] mb-4 flex items-center">
                                <FaClock className="mr-2 text-[#0D9488]" />
                                Estimasi Waktu Proses
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {waktuInfo.map((w, i) => (
                                    <div key={i} className={`${w.bg} p-4 rounded-lg text-center`}>
                                        <p className="text-sm text-gray-600">{w.label}</p>
                                        <p className={`text-2xl font-bold ${w.color}`}>{w.value}</p>
                                    </div>
                                ))}
                            </div>
                            {setting?.waktu_total_catatan && (
                                <div className="mt-4 p-4 bg-yellow-50 rounded-lg">
                                    <p className="text-yellow-800 flex items-center">
                                        <FaInfoCircle className="mr-2" />
                                        {setting.waktu_total_catatan}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* ============ TAB: FORMULIR ============ */}
                {activeTab === 'formulir' && (
                    <div>
                        {/* Download Formulir */}
                        <div className="card-glass mb-8">
                            <h3 className="text-xl font-bold text-[#1E3A8A] mb-4">
                                Download Formulir
                            </h3>

                            {files.length === 0 ? (
                                <p className="text-gray-500 text-center py-4">
                                    Belum ada formulir yang tersedia
                                </p>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {files.map((file) => (
                                        <a
                                            key={file.id}
                                            href={getFileUrl(file.file_path)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                                        >
                                            <FaFilePdf className="text-red-500 text-3xl mr-3 flex-shrink-0" />
                                            <div className="min-w-0">
                                                <p className="font-semibold text-gray-800 truncate">
                                                    {file.judul}
                                                </p>
                                                <p className="text-sm text-gray-500">
                                                    {file.file_type?.toUpperCase()} - {formatFileSize(file.file_size)}
                                                </p>
                                            </div>
                                        </a>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Online Form */}
                        <div className="card-glass" id="form-online">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-xl font-bold text-[#1E3A8A]">Formulir Online</h3>
                                <button
                                    onClick={() => {
                                        setShowForm(!showForm);
                                        setSubmitResult(null);
                                        setSubmitError('');
                                        setFormErrors({});
                                    }}
                                    className="btn-primary"
                                >
                                    {showForm ? 'Tutup Form' : 'Isi Form Online'}
                                </button>
                            </div>

                            {showForm && (
                                <>
                                    {/* ✅ Hasil sukses */}
                                    {submitResult && (
                                        <div className="mb-6 p-4 bg-green-50 border-2 border-green-500 rounded-lg">
                                            <div className="flex items-start">
                                                <FaCheckCircle className="text-green-500 text-2xl mr-3 flex-shrink-0 mt-1" />
                                                <div className="flex-1">
                                                    <h4 className="font-bold text-green-800 mb-1">
                                                        Permohonan Berhasil Dikirim!
                                                    </h4>
                                                    <p className="text-green-700 text-sm mb-3">
                                                        Simpan nomor registrasi di bawah ini untuk cek status permohonan Anda:
                                                    </p>
                                                    <div className="bg-white border-2 border-green-500 rounded-lg px-4 py-3 inline-block">
                                                        <p className="text-xs text-gray-500 mb-1">Nomor Registrasi</p>
                                                        <p className="text-2xl font-bold text-green-700 tracking-wider">
                                                            {submitResult.nomor_registrasi}
                                                        </p>
                                                    </div>
                                                    <p className="text-xs text-green-600 mt-3">
                                                        Status awal: <strong className="capitalize">{submitResult.status}</strong>
                                                        {submitResult.created_at && (
                                                            <> • Tanggal: {new Date(submitResult.created_at).toLocaleString('id-ID')}</>
                                                        )}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* ✅ Error global */}
                                    {submitError && (
                                        <div className="mb-6 p-4 bg-red-50 border border-red-300 rounded-lg flex items-start">
                                            <FaExclamationTriangle className="text-red-500 text-xl mr-3 flex-shrink-0 mt-0.5" />
                                            <p className="text-red-700 text-sm">{submitError}</p>
                                        </div>
                                    )}

                                    <form onSubmit={handleFormSubmit} className="space-y-6">
                                        {/* 🍯 Honeypot — WAJIB KOSONG. Bot akan mengisi otomatis. */}
                                        <input
                                            type="text"
                                            name="website_url"
                                            value={formData.website_url}
                                            onChange={handleFormChange}
                                            style={{ display: 'none', position: 'absolute', left: '-9999px' }}
                                            tabIndex="-1"
                                            autoComplete="off"
                                            aria-hidden="true"
                                        />

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Nama Lengkap <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    name="nama_lengkap"
                                                    value={formData.nama_lengkap}
                                                    onChange={handleFormChange}
                                                    maxLength={255}
                                                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0D9488] focus:border-transparent ${formErrors.nama_lengkap ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                                        }`}
                                                    placeholder="Masukkan nama lengkap"
                                                    required
                                                />
                                                {formErrors.nama_lengkap && (
                                                    <p className="text-red-500 text-xs mt-1">{formErrors.nama_lengkap}</p>
                                                )}
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    NIK (16 digit) <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    name="nik"
                                                    value={formData.nik}
                                                    onChange={(e) => {
                                                        // Hanya izinkan angka
                                                        const val = e.target.value.replace(/\D/g, '').slice(0, 16);
                                                        setFormData(prev => ({ ...prev, nik: val }));
                                                        if (formErrors.nik) setFormErrors(prev => ({ ...prev, nik: '' }));
                                                    }}
                                                    maxLength={16}
                                                    inputMode="numeric"
                                                    pattern="[0-9]*"
                                                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0D9488] focus:border-transparent ${formErrors.nik ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                                        }`}
                                                    placeholder="Masukkan 16 digit NIK"
                                                    required
                                                />
                                                <p className="text-xs text-gray-400 mt-1">
                                                    {formData.nik.length}/16 digit
                                                </p>
                                                {formErrors.nik && (
                                                    <p className="text-red-500 text-xs mt-1">{formErrors.nik}</p>
                                                )}
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Nomor Telepon <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="tel"
                                                    name="nomor_telepon"
                                                    value={formData.nomor_telepon}
                                                    onChange={handleFormChange}
                                                    maxLength={20}
                                                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0D9488] focus:border-transparent ${formErrors.nomor_telepon ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                                        }`}
                                                    placeholder="Contoh: 08123456789"
                                                    required
                                                />
                                                {formErrors.nomor_telepon && (
                                                    <p className="text-red-500 text-xs mt-1">{formErrors.nomor_telepon}</p>
                                                )}
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Email
                                                </label>
                                                <input
                                                    type="email"
                                                    name="email"
                                                    value={formData.email}
                                                    onChange={handleFormChange}
                                                    maxLength={255}
                                                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0D9488] focus:border-transparent ${formErrors.email ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                                        }`}
                                                    placeholder="nama@email.com"
                                                />
                                                {formErrors.email && (
                                                    <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>
                                                )}
                                            </div>

                                            <div className="md:col-span-2">
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Alamat Lengkap <span className="text-red-500">*</span>
                                                </label>
                                                <textarea
                                                    name="alamat_lengkap"
                                                    value={formData.alamat_lengkap}
                                                    onChange={handleFormChange}
                                                    rows="2"
                                                    maxLength={1000}
                                                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0D9488] focus:border-transparent ${formErrors.alamat_lengkap ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                                        }`}
                                                    placeholder="Masukkan alamat lengkap sesuai KTP"
                                                    required
                                                ></textarea>
                                                {formErrors.alamat_lengkap && (
                                                    <p className="text-red-500 text-xs mt-1">{formErrors.alamat_lengkap}</p>
                                                )}
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Lokasi Tanah yang Dimohon <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    name="lokasi_tanah"
                                                    value={formData.lokasi_tanah}
                                                    onChange={handleFormChange}
                                                    maxLength={500}
                                                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0D9488] focus:border-transparent ${formErrors.lokasi_tanah ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                                        }`}
                                                    placeholder="Contoh: Jl. Soekarno Hatta No. 5"
                                                    required
                                                />
                                                {formErrors.lokasi_tanah && (
                                                    <p className="text-red-500 text-xs mt-1">{formErrors.lokasi_tanah}</p>
                                                )}
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Luas Tanah (m²) <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="number"
                                                    name="luas_tanah"
                                                    value={formData.luas_tanah}
                                                    onChange={handleFormChange}
                                                    min="1"
                                                    max="999999"
                                                    step="0.01"
                                                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0D9488] focus:border-transparent ${formErrors.luas_tanah ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                                        }`}
                                                    placeholder="Contoh: 100"
                                                    required
                                                />
                                                {formErrors.luas_tanah && (
                                                    <p className="text-red-500 text-xs mt-1">{formErrors.luas_tanah}</p>
                                                )}
                                            </div>

                                            <div className="md:col-span-2">
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Tujuan Penggunaan Tanah <span className="text-red-500">*</span>
                                                </label>
                                                <textarea
                                                    name="tujuan_penggunaan"
                                                    value={formData.tujuan_penggunaan}
                                                    onChange={handleFormChange}
                                                    rows="3"
                                                    maxLength={2000}
                                                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0D9488] focus:border-transparent ${formErrors.tujuan_penggunaan ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                                        }`}
                                                    placeholder="Jelaskan secara singkat tujuan penggunaan tanah..."
                                                    required
                                                ></textarea>
                                                {formErrors.tujuan_penggunaan && (
                                                    <p className="text-red-500 text-xs mt-1">{formErrors.tujuan_penggunaan}</p>
                                                )}
                                            </div>

                                            <div className="md:col-span-2">
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Upload Dokumen Pendukung (Opsional, maks. 5 file)
                                                </label>
                                                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-[#0D9488] transition-colors">
                                                    <input
                                                        type="file"
                                                        id="file-pendukung"
                                                        accept=".pdf,.jpg,.jpeg,.png"
                                                        multiple
                                                        onChange={handleFormFilesChange}
                                                        className="hidden"
                                                    />
                                                    <label htmlFor="file-pendukung" className="cursor-pointer block">
                                                        <FaDownload className="mx-auto text-3xl text-gray-400 mb-2" />
                                                        <p className="text-sm text-gray-500">
                                                            Klik untuk memilih file
                                                        </p>
                                                        <p className="text-xs text-gray-400 mt-1">
                                                            PDF, JPG, PNG • Maks 5MB per file
                                                        </p>
                                                    </label>
                                                </div>
                                                {formFiles.length > 0 && (
                                                    <div className="mt-2 space-y-1">
                                                        {formFiles.map((f, i) => (
                                                            <p key={i} className="text-xs text-green-600 flex items-center">
                                                                <FaCheckCircle className="mr-1" size={10} />
                                                                {f.name} ({(f.size / 1024).toFixed(1)} KB)
                                                            </p>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="flex justify-end space-x-4">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setShowForm(false);
                                                    setSubmitResult(null);
                                                    setSubmitError('');
                                                    setFormErrors({});
                                                }}
                                                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                                            >
                                                Batal
                                            </button>
                                            <button
                                                type="submit"
                                                disabled={submitting}
                                                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
                                            >
                                                {submitting ? (
                                                    <>
                                                        <FaSpinner className="animate-spin mr-2" />
                                                        Mengirim...
                                                    </>
                                                ) : (
                                                    <>
                                                        <FaPaperPlane className="mr-2" />
                                                        Kirim Permohonan
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </form>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* ============ FAQ SECTION (Dari API) ============ */}
            {faqList.length > 0 && (
                <div className="mt-12">
                    <h2 className="text-2xl font-bold text-[#1E3A8A] mb-6">
                        Pertanyaan Umum (FAQ)
                    </h2>
                    <div className="space-y-4">
                        {faqList.map((faq, index) => (
                            <details key={index} className="card-glass group">
                                <summary className="flex justify-between items-center cursor-pointer list-none">
                                    <span className="text-lg font-semibold text-[#1E3A8A]">
                                        {faq.question}
                                    </span>
                                    <span className="text-[#0D9488] group-open:rotate-180 transition-transform">
                                        ▼
                                    </span>
                                </summary>
                                <p className="mt-3 text-gray-600">{faq.answer}</p>
                            </details>
                        ))}
                    </div>
                </div>
            )}

            {/* ============ CONTACT SECTION ============ */}
            <div className="mt-12 card-glass bg-gradient-to-r from-[#1E3A8A] to-[#0D9488] text-white">
                <div className="text-center">
                    <h3 className="text-2xl font-bold mb-4">Butuh Bantuan?</h3>
                    <p className="mb-6">
                        Hubungi kami untuk informasi lebih lanjut tentang Perizinan Aset IPT
                    </p>
                    <div className="flex flex-col md:flex-row justify-center gap-4">
                        <button className="bg-white text-[#1E3A8A] px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-flex items-center justify-center">
                            <FaPhone className="mr-2" /> Hubungi Kami
                        </button>
                        <button className="bg-[#0F172A] text-white px-6 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition-colors inline-flex items-center justify-center">
                            <FaWhatsapp className="mr-2" /> Chat WhatsApp
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PerizinanIPT;